import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowDownToLine,
  ArrowLeftRight,
  ArrowUpFromLine,
  Boxes,
  ClipboardCheck,
  ClipboardList,
  MapPin,
  PackageSearch,
  Pencil,
  Plus,
  RefreshCw,
  Settings2,
  Trash2,
  TriangleAlert,
  Warehouse,
} from 'lucide-react';
import type { RecordDeletionGateway, TenantSummary } from '@forq/contracts';
import type {
  WarehouseArticleOption,
  WarehouseGateway,
  WarehouseLocationRecord,
  WarehouseMovementRecord,
  WarehouseMovementType,
  WarehouseOverview,
  WarehouseReplenishmentRecord,
  WarehouseStockFilter,
  WarehouseStockPage,
} from '@forq/warehouse-data';
import {
  DataTable,
  type DataTableColumn,
  CustomizableMetrics,
  EmptyState,
  FilterBar,
  MetricTile,
  RecordDeleteButton,
  SearchSelect,
  StatusPill,
  Tabs,
  type TabItem,
  WorkspacePage,
  confirmDiscard,
  describeDeleteOutcome,
  useContextMenu,
  useRecordDeleteFlow,
  type ContextMenuItem,
} from '@forq/ui';
import { InventurSection } from './InventurSection';

interface WarehousePageProps {
  readonly tenant: TenantSummary;
  readonly mode: 'demo' | 'supabase';
  readonly permissions: ReadonlySet<string>;
  readonly enabledFeatureIds: readonly string[];
  readonly gateway: WarehouseGateway;
  readonly deletionGateway: RecordDeletionGateway;
}

type WarehouseSection = 'overview' | 'stock' | 'movements' | 'locations' | 'replenishment' | 'inventur';

const sectionSlugs: Record<WarehouseSection, string> = {
  overview: 'uebersicht',
  stock: 'bestaende',
  movements: 'bewegungen',
  locations: 'lagerplaetze',
  replenishment: 'beschaffung',
  inventur: 'inventur',
};

const emptyOverview: WarehouseOverview = {
  stockedItems: 0,
  lowStockItems: 0,
  negativeStockItems: 0,
  activeLocations: 0,
  movementsToday: 0,
  openReplenishmentItems: 0,
};
const emptyStock: WarehouseStockPage = { items: [], total: 0, page: 1, pageSize: 50 };

function messageOf(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Lagerdaten konnten nicht verarbeitet werden.';
}

function value(form: FormData, key: string): string {
  return String(form.get(key) ?? '').trim();
}

function numberValue(form: FormData, key: string): number {
  return Number(value(form, key).replace(',', '.'));
}

function movementLabel(type: WarehouseMovementType): string {
  if (type === 'receipt') return 'Zugang';
  if (type === 'issue') return 'Abgang';
  if (type === 'transfer') return 'Umlagerung';
  return 'Korrektur';
}

function movementIcon(type: WarehouseMovementType) {
  if (type === 'receipt') return ArrowDownToLine;
  if (type === 'issue') return ArrowUpFromLine;
  if (type === 'transfer') return ArrowLeftRight;
  return Settings2;
}

function unitLabel(unit: string): string {
  const labels: Record<string, string> = {
    piece: 'Stk.', hour: 'Std.', kilometer: 'km', 'flat-rate': 'Pausch.', meter: 'm', kilogram: 'kg', liter: 'l',
  };
  return labels[unit] ?? unit;
}

function quantity(valueToFormat: number): string {
  return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 }).format(valueToFormat);
}

function money(valueToFormat: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(valueToFormat);
}

type WarehouseStockRow = WarehouseStockPage['items'][number];

function stockRowTone(item: WarehouseStockRow): 'danger' | 'warning' | undefined {
  if (item.negativeStock) return 'danger';
  if (item.lowStock) return 'warning';
  return undefined;
}

const stockColumns: readonly DataTableColumn<WarehouseStockRow>[] = [
  {
    key: 'item',
    header: 'Artikel',
    width: 'minmax(0, 1.8fr)',
    render: (item) => (
      <span className="forq-stack">
        <strong>{item.itemName}</strong>
        <small>{item.itemNumber}{item.preferredSupplierName ? ` · ${item.preferredSupplierName}` : ''}</small>
      </span>
    ),
  },
  {
    key: 'location',
    header: 'Lagerplatz',
    width: 'minmax(0, 1fr)',
    render: (item) => (
      <span className="forq-stack"><strong>{item.locationCode}</strong><small>{item.locationName}</small></span>
    ),
  },
  { key: 'onHand', header: 'Ist', align: 'end', width: '108px', render: (item) => <span className="forq-cell-num">{quantity(item.onHand)} {unitLabel(item.unit)}</span> },
  { key: 'reserved', header: 'Reserviert', align: 'end', width: '96px', render: (item) => <span className="forq-cell-num">{quantity(item.reserved)}</span> },
  { key: 'available', header: 'Verfügbar', align: 'end', width: '96px', render: (item) => <span className="forq-cell-num"><strong>{quantity(item.available)}</strong></span> },
  { key: 'minimum', header: 'Mindest', align: 'end', width: '96px', render: (item) => <span className="forq-cell-num">{quantity(item.minimumStock)}</span> },
  {
    key: 'status',
    header: 'Status',
    width: '132px',
    render: (item) => (
      <StatusPill
        label={item.negativeStock ? 'Negativ' : item.lowStock ? 'Unterdeckung' : 'Gedeckt'}
        tone={item.negativeStock ? 'danger' : item.lowStock ? 'warning' : 'done'}
      />
    ),
  },
];

export function WarehousePage({ tenant, mode, permissions, enabledFeatureIds, gateway, deletionGateway }: WarehousePageProps) {
  const navigate = useNavigate();
  const { section: sectionSlug } = useParams();
  const availableSections = useMemo(() => ([
    { id: 'overview' as const, feature: 'warehouse.overview', label: 'Übersicht', icon: Warehouse },
    { id: 'stock' as const, feature: 'warehouse.stock', label: 'Bestände', icon: Boxes },
    { id: 'movements' as const, feature: 'warehouse.movements', label: 'Bewegungen', icon: ArrowLeftRight },
    { id: 'locations' as const, feature: 'warehouse.locations', label: 'Lagerplätze', icon: MapPin },
    { id: 'replenishment' as const, feature: 'warehouse.replenishment', label: 'Beschaffung', icon: ClipboardList },
    { id: 'inventur' as const, feature: 'warehouse.inventory', label: 'Inventur', icon: ClipboardCheck },
  ]).filter((section) => enabledFeatureIds.includes(section.feature)), [enabledFeatureIds]);

  const [activeSection, setActiveSection] = useState<WarehouseSection>(availableSections[0]?.id ?? 'overview');
  const [overview, setOverview] = useState<WarehouseOverview>(emptyOverview);
  const [locations, setLocations] = useState<readonly WarehouseLocationRecord[]>([]);
  const [articles, setArticles] = useState<readonly WarehouseArticleOption[]>([]);
  const [stock, setStock] = useState<WarehouseStockPage>(emptyStock);
  const [movements, setMovements] = useState<readonly WarehouseMovementRecord[]>([]);
  const [replenishment, setReplenishment] = useState<readonly WarehouseReplenishmentRecord[]>([]);
  const [query, setQuery] = useState('');
  const [stockFilter, setStockFilter] = useState<WarehouseStockFilter>('all');
  const [locationFilter, setLocationFilter] = useState<string | 'all'>('all');
  const [loading, setLoading] = useState(true);
  const [stockLoading, setStockLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [movementOpen, setMovementOpen] = useState(false);
  const [movementType, setMovementType] = useState<WarehouseMovementType>('receipt');
  const [locationEditor, setLocationEditor] = useState<WarehouseLocationRecord | 'new' | null>(null);

  const canPost = permissions.has('warehouse.movements.post');
  const canManageLocations = permissions.has('warehouse.locations.manage');
  const canDeleteLocations = permissions.has('warehouse.locations.delete');
  const canViewPurchasePrices = permissions.has('masterdata.purchase_prices.view');

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [nextOverview, nextLocations, nextArticles, nextMovements, nextReplenishment] = await Promise.all([
        gateway.getOverview(tenant.id),
        gateway.listLocations(tenant.id, true),
        gateway.searchArticleOptions(tenant.id),
        gateway.listMovements(tenant.id, 100),
        gateway.listReplenishment(tenant.id),
      ]);
      setOverview(nextOverview);
      setLocations(nextLocations);
      setArticles(nextArticles);
      setMovements(nextMovements);
      setReplenishment(nextReplenishment);
    } catch (loadError) {
      setError(messageOf(loadError));
    } finally {
      setLoading(false);
    }
  }, [gateway, tenant.id]);

  const loadStock = useCallback(async () => {
    setStockLoading(true);
    try {
      setStock(await gateway.searchStock({
        tenantId: tenant.id,
        query,
        filter: stockFilter,
        locationId: locationFilter,
        pageSize: 50,
      }));
    } catch (loadError) {
      setStock(emptyStock);
      setError(messageOf(loadError));
    } finally {
      setStockLoading(false);
    }
  }, [gateway, locationFilter, query, stockFilter, tenant.id]);

  const { open: openWarehouseMenu, menu: warehouseMenu } = useContextMenu();
  const { requestDelete: requestLocationDelete, deleteDialog: locationDeleteDialog } = useRecordDeleteFlow(deletionGateway, tenant.id);
  const [prefillItemId, setPrefillItemId] = useState('');
  const openMovementFor = useCallback((itemId: string) => { setPrefillItemId(itemId); setMovementOpen(true); }, []);
  const stockMenuItems = useCallback((row: WarehouseStockRow): ContextMenuItem[] => (
    canPost ? [{ label: 'Bewegung buchen', icon: <ArrowLeftRight size={15} />, onSelect: () => openMovementFor(row.itemId) }] : []
  ), [canPost, openMovementFor]);
  const locationMenuItems = useCallback((location: WarehouseLocationRecord): ContextMenuItem[] => {
    const items: ContextMenuItem[] = [
      { label: 'Bearbeiten', icon: <Pencil size={15} />, disabled: !canManageLocations, onSelect: () => setLocationEditor(location) },
    ];
    if (!location.defaultLocation) items.push({
      label: 'Löschen', icon: <Trash2 size={15} />, danger: true, separatorBefore: true, disabled: !canDeleteLocations,
      onSelect: () => requestLocationDelete({
        entity: 'warehouse-location', id: location.id, noun: 'Lagerplatz', label: `${location.code} · ${location.name}`,
        onDone: (outcome) => { setNotice(describeDeleteOutcome(outcome, 'Lagerplatz', `${location.code} · ${location.name}`)); void loadAll(); void loadStock(); },
      }),
    });
    return items;
  }, [canManageLocations, canDeleteLocations, requestLocationDelete, loadAll, loadStock]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  useEffect(() => {
    const timer = window.setTimeout(() => void loadStock(), 160);
    return () => window.clearTimeout(timer);
  }, [loadStock]);

  useEffect(() => {
    if (!availableSections.some((section) => section.id === activeSection)) {
      setActiveSection(availableSections[0]?.id ?? 'overview');
    }
  }, [activeSection, availableSections]);

  // The section slug in the URL (/lager/:section) selects the tab — deep-linkable.
  useEffect(() => {
    const target = (Object.keys(sectionSlugs) as WarehouseSection[]).find((id) => sectionSlugs[id] === sectionSlug);
    if (target && availableSections.some((entry) => entry.id === target)) setActiveSection(target);
  }, [sectionSlug, availableSections]);

  const submitMovement = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSaving(true);
    setError(null);
    try {
      const type = value(form, 'type') as WarehouseMovementType;
      await gateway.postMovement({
        tenantId: tenant.id,
        type,
        itemId: value(form, 'itemId'),
        quantity: numberValue(form, 'quantity'),
        sourceLocationId: type === 'issue' || type === 'transfer' ? value(form, 'sourceLocationId') : null,
        targetLocationId: type === 'receipt' || type === 'transfer' || type === 'adjustment' ? value(form, 'targetLocationId') : null,
        reason: value(form, 'reason'),
        reference: value(form, 'reference'),
      });
      setMovementOpen(false);
      await Promise.all([loadAll(), loadStock()]);
    } catch (saveError) {
      setError(messageOf(saveError));
    } finally {
      setSaving(false);
    }
  }, [gateway, loadAll, loadStock, tenant.id]);

  const submitLocation = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const current = locationEditor === 'new' ? null : locationEditor;
    setSaving(true);
    setError(null);
    try {
      await gateway.saveLocation({
        tenantId: tenant.id,
        ...(current ? { locationId: current.id } : {}),
        code: value(form, 'code'),
        name: value(form, 'name'),
        description: value(form, 'description'),
        active: form.get('active') === 'on',
        defaultLocation: form.get('defaultLocation') === 'on',
      });
      setLocationEditor(null);
      await Promise.all([loadAll(), loadStock()]);
    } catch (saveError) {
      setError(messageOf(saveError));
    } finally {
      setSaving(false);
    }
  }, [gateway, loadAll, loadStock, locationEditor, tenant.id]);

  if (availableSections.length === 0) {
    return <EmptyState eyebrow="Lager & Bestand" title="Für diesen Mandanten ist keine Lagerfunktion freigegeben." description="Die Bereiche werden aus Rollen und einzelnen Funktionsrechten ermittelt." />;
  }

  const sectionTabs: readonly TabItem[] = availableSections.map((section) => {
    const Icon = section.icon;
    return { id: section.id, label: section.label, icon: <Icon size={15} /> };
  });

  return (
    <WorkspacePage
      customizeKey="warehouse-overview"
      eyebrow="FORQ Warehouse"
      title="Lager, Bestand & Beschaffung"
      description={`${tenant.name} · Bestände und Bewegungen sind strikt mandantenbezogen`}
      actions={(
        <>
          <span className={`warehouse-mode ${mode === 'demo' ? 'is-demo' : ''}`}>{mode === 'demo' ? 'Demo – Änderungen bis zum Neuladen' : 'Supabase verbunden'}</span>
          <button type="button" className="warehouse-secondary" onClick={() => void Promise.all([loadAll(), loadStock()])} disabled={loading || stockLoading}><RefreshCw size={16} /> Aktualisieren</button>
          <button type="button" className="warehouse-primary" onClick={() => setMovementOpen(true)} disabled={!canPost}><Plus size={17} /> Bewegung buchen</button>
        </>
      )}
      toolbar={<Tabs items={sectionTabs} activeId={activeSection} onSelect={(id) => navigate(`/lager/${sectionSlugs[id as WarehouseSection]}`)} ariaLabel="Lagerbereiche" />}
    >

      {error && !movementOpen && !locationEditor ? <div className="warehouse-error" role="alert"><span>{error}</span><button type="button" onClick={() => setError(null)}>Schließen</button></div> : null}

      {notice ? <div className="warehouse-notice" role="status"><span>{notice}</span><button type="button" onClick={() => setNotice(null)}>Schließen</button></div> : null}

      {loading ? <div className="warehouse-loading">Lagerdaten werden geladen …</div> : null}

      {!loading && activeSection === 'overview' ? (
        <section className="warehouse-panel">
          <div className="warehouse-panel__heading"><div><span className="warehouse-eyebrow">Aktuelle Lage</span><h2>Lagerübersicht</h2></div></div>
          <div className="forq-metric-row">
            <CustomizableMetrics
              pageKey="warehouse-overview"
              tiles={[
                { key: 'stocked', node: <MetricTile label="Artikel mit Bestand" value={overview.stockedItems} icon={<Boxes size={16} />} /> },
                { key: 'lowstock', node: <MetricTile label="Unter Mindestbestand" value={overview.lowStockItems} tone={overview.lowStockItems > 0 ? 'warning' : 'default'} icon={<TriangleAlert size={16} />} /> },
                { key: 'negative', node: <MetricTile label="Negative Bestände" value={overview.negativeStockItems} tone={overview.negativeStockItems > 0 ? 'danger' : 'default'} icon={<PackageSearch size={16} />} /> },
                { key: 'locations', node: <MetricTile label="Aktive Lagerplätze" value={overview.activeLocations} icon={<MapPin size={16} />} /> },
                { key: 'movements', node: <MetricTile label="Bewegungen heute" value={overview.movementsToday} icon={<ArrowLeftRight size={16} />} /> },
                { key: 'replenish', node: <MetricTile label="Beschaffungsvorschläge" value={overview.openReplenishmentItems} tone={overview.openReplenishmentItems > 0 ? 'warning' : 'default'} icon={<ClipboardList size={16} />} /> },
              ]}
            />
          </div>
          <div className="warehouse-overview-grid">
            <article>
              <span className="warehouse-eyebrow">Letzte Bewegung</span>
              {movements[0] ? <><h3>{movementLabel(movements[0].type)} · {movements[0].itemName}</h3><p>{quantity(movements[0].quantity)} {movements[0].reference ? `· ${movements[0].reference}` : ''}</p></> : <p>Noch keine Bewegung vorhanden.</p>}
            </article>
            <article>
              <span className="warehouse-eyebrow">Nächster Handlungsbedarf</span>
              {replenishment[0] ? <><h3>{replenishment[0].itemName}</h3><p>{quantity(replenishment[0].suggestedQuantity)} {unitLabel(replenishment[0].unit)} nachbeschaffen · {replenishment[0].supplierName || 'Lieferant fehlt'}</p></> : <p>Alle Mindestbestände sind gedeckt.</p>}
            </article>
          </div>
        </section>
      ) : null}

      {!loading && activeSection === 'stock' ? (
        <section className="warehouse-panel">
          <div className="warehouse-panel__heading"><div><span className="warehouse-eyebrow">Bestandsübersicht</span><h2>Artikel je Lagerplatz</h2></div><span>{stockLoading ? 'Lädt …' : `${stock.total} Bestandszeilen`}</span></div>
          <FilterBar search={{ value: query, onChange: setQuery, placeholder: 'Artikel, Nummer, Lagerplatz oder Lieferant suchen …' }}>
            <SearchSelect value={stockFilter} onChange={(next) => setStockFilter(next as WarehouseStockFilter)} ariaLabel="Bestandsfilter" options={[{ value: 'all', label: 'Alle Bestände' }, { value: 'low', label: 'Unter Mindestbestand' }, { value: 'negative', label: 'Negative Bestände' }]} />
            <SearchSelect value={locationFilter} onChange={(next) => setLocationFilter(next)} ariaLabel="Lagerplatzfilter" options={[{ value: 'all', label: 'Alle Lagerplätze' }, ...locations.filter((location) => location.active).map((location) => ({ value: location.id, label: `${location.code} · ${location.name}` }))]} />
          </FilterBar>
          <DataTable
            columns={stockColumns}
            rows={stock.items}
            rowKey={(item) => `${item.itemId}-${item.locationId}`}
            rowTone={stockRowTone}
            onRowContext={(event, row) => openWarehouseMenu(event, stockMenuItems(row))}
            loading={stockLoading}
            loadingLabel="Bestände werden geladen …"
            emptyLabel="Keine Bestände für diesen Filter gefunden."
            ariaLabel="Bestandsliste"
          />
        </section>
      ) : null}

      {!loading && activeSection === 'movements' ? (
        <section className="warehouse-panel">
          <div className="warehouse-panel__heading"><div><span className="warehouse-eyebrow">Unveränderliches Bewegungsjournal</span><h2>Bestandsbewegungen</h2></div><button type="button" className="warehouse-primary" onClick={() => { setPrefillItemId(''); setMovementOpen(true); }} disabled={!canPost}><Plus size={16} /> Bewegung buchen</button></div>
          <div className="warehouse-movement-list">
            {movements.map((movement) => { const Icon = movementIcon(movement.type); return <article key={movement.id}><span className={`warehouse-movement-icon is-${movement.type}`}><Icon size={17} /></span><div><strong>{movementLabel(movement.type)} · {movement.itemName}</strong><small>{movement.itemNumber} · {movement.sourceLocationCode || 'Extern'} → {movement.targetLocationCode || 'Extern'}</small><p>{movement.reason}{movement.reference ? ` · ${movement.reference}` : ''}</p></div><div><strong>{quantity(movement.quantity)}</strong><small>{new Date(movement.occurredAt).toLocaleString('de-DE')} · {movement.actor}</small></div></article>; })}
            {movements.length === 0 ? <p className="warehouse-empty">Noch keine Bewegungen vorhanden.</p> : null}
          </div>
        </section>
      ) : null}

      {!loading && activeSection === 'locations' ? (
        <section className="warehouse-panel">
          <div className="warehouse-panel__heading"><div><span className="warehouse-eyebrow">Physische Lagerstruktur</span><h2>Lagerplätze</h2></div><button type="button" className="warehouse-primary" onClick={() => setLocationEditor('new')} disabled={!canManageLocations}><Plus size={16} /> Lagerplatz</button></div>
          <div className="warehouse-location-grid">
            {locations.map((location) => <article key={location.id} className={!location.active ? 'is-inactive' : ''} onContextMenu={(event) => openWarehouseMenu(event, locationMenuItems(location))}><div><span className="warehouse-location-code">{location.code}</span>{location.defaultLocation ? <em>Standard</em> : null}</div><h3>{location.name}</h3><p>{location.description || 'Keine Beschreibung hinterlegt.'}</p><footer><span>{location.stockedItemCount} Artikel mit Bestand</span><div className="warehouse-location-actions"><button type="button" onClick={() => setLocationEditor(location)} disabled={!canManageLocations}>Bearbeiten</button>{!location.defaultLocation ? <RecordDeleteButton className="warehouse-location-delete" gateway={deletionGateway} tenantId={tenant.id} entity="warehouse-location" id={location.id} noun="Lagerplatz" label={`${location.code} · ${location.name}`} canDelete={canDeleteLocations} onDone={(outcome) => { setNotice(describeDeleteOutcome(outcome, 'Lagerplatz', `${location.code} · ${location.name}`)); void loadAll(); void loadStock(); }} /> : null}</div></footer></article>)}
          </div>
        </section>
      ) : null}

      {!loading && activeSection === 'replenishment' ? (
        <section className="warehouse-panel">
          <div className="warehouse-panel__heading"><div><span className="warehouse-eyebrow">Automatisch aus Mindestbestand</span><h2>Beschaffungsvorschläge</h2></div><span>{replenishment.length} offene Positionen</span></div>
          <div className="warehouse-table-wrap">
            <table className="warehouse-table"><thead><tr><th>Artikel</th><th>Verfügbar</th><th>Minimum</th><th>Vorschlag</th><th>Bevorzugter Lieferant</th><th>Preis / Lieferzeit</th><th>Priorität</th></tr></thead><tbody>
              {replenishment.map((item) => <tr key={item.itemId} className={item.urgency === 'critical' ? 'is-danger' : 'is-warning'}><td><strong>{item.itemName}</strong><small>{item.itemNumber}</small></td><td>{quantity(item.available)} {unitLabel(item.unit)}</td><td>{quantity(item.minimumStock)}</td><td><strong>{quantity(item.suggestedQuantity)} {unitLabel(item.unit)}</strong></td><td><strong>{item.supplierName || 'Nicht zugeordnet'}</strong><small>{item.supplierItemNumber}</small></td><td>{canViewPurchasePrices && item.purchasePrice > 0 ? money(item.purchasePrice) : 'Geschützt'}<small>{item.leadTimeDays === null ? 'Lieferzeit fehlt' : `${item.leadTimeDays} Tage`}</small></td><td><span className={`warehouse-status is-${item.urgency === 'critical' ? 'danger' : 'warning'}`}>{item.urgency === 'critical' ? 'Kritisch' : 'Niedrig'}</span></td></tr>)}
            </tbody></table>
            {replenishment.length === 0 ? <p className="warehouse-empty">Aktuell besteht kein Beschaffungsbedarf.</p> : null}
          </div>
          <p className="warehouse-hint">Diese Positionen sind zunächst Vorschläge. Bestellt wird unter „Ersatzteile" -- immer aus einem Vorgang heraus, für Verbrauchsmaterial aus dem internen Sammelvorgang.</p>
        </section>
      ) : null}

      {!loading && activeSection === 'inventur' ? (
        <InventurSection tenantId={tenant.id} gateway={gateway} locations={locations} canPost={canPost} onPosted={() => { void loadAll(); void loadStock(); }} />
      ) : null}

      {movementOpen ? (
        <div className="warehouse-modal" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && confirmDiscard()) setMovementOpen(false); }}>
          <form className="warehouse-modal__panel" onSubmit={submitMovement}>
            <span className="warehouse-eyebrow">Bestandsjournal</span><h2>Bewegung buchen</h2><p>Die Buchung verändert Bestände atomar und bleibt anschließend im Journal nachvollziehbar.</p>
            <div className="warehouse-form-grid">
              <label>Bewegungsart<SearchSelect name="type" value={movementType} onChange={(next) => setMovementType(next as WarehouseMovementType)} options={[{ value: 'receipt', label: 'Zugang' }, { value: 'issue', label: 'Abgang' }, { value: 'transfer', label: 'Umlagerung' }, { value: 'adjustment', label: 'Korrektur' }]} /></label>
              <label>Artikel<SearchSelect key={prefillItemId} name="itemId" defaultValue={prefillItemId} placeholder="Artikel auswählen …" options={articles.map((article) => ({ value: article.id, label: `${article.number} · ${article.name}` }))} /></label>
              <label>Menge<input name="quantity" type="number" step="0.001" required placeholder={movementType === 'adjustment' ? 'auch negativ möglich' : 'positive Menge'} /></label>
              {(movementType === 'issue' || movementType === 'transfer') ? <label>Quelllagerplatz<SearchSelect name="sourceLocationId" defaultValue="" placeholder="Auswählen …" options={locations.filter((location) => location.active).map((location) => ({ value: location.id, label: `${location.code} · ${location.name}` }))} /></label> : null}
              {(movementType === 'receipt' || movementType === 'transfer' || movementType === 'adjustment') ? <label>Ziellagerplatz<SearchSelect name="targetLocationId" defaultValue="" placeholder="Auswählen …" options={locations.filter((location) => location.active).map((location) => ({ value: location.id, label: `${location.code} · ${location.name}` }))} /></label> : null}
              <label>Referenz<input name="reference" placeholder="Auftrag, Lieferschein, Inventur …" /></label>
              <label className="is-wide">Buchungsgrund<textarea name="reason" rows={3} required placeholder="Warum wird der Bestand verändert?" /></label>
            </div>
            {error ? <div className="warehouse-modal__error" role="alert">{error}</div> : null}
            <div className="warehouse-modal__actions"><button type="button" className="warehouse-secondary" onClick={() => setMovementOpen(false)}>Abbrechen</button><button type="submit" className="warehouse-primary" disabled={saving}>{saving ? 'Bucht …' : 'Verbindlich buchen'}</button></div>
          </form>
        </div>
      ) : null}

      {locationEditor ? (
        <div className="warehouse-modal" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && confirmDiscard()) setLocationEditor(null); }}>
          <form className="warehouse-modal__panel is-small" onSubmit={submitLocation}>
            <span className="warehouse-eyebrow">Lagerstruktur</span><h2>{locationEditor === 'new' ? 'Lagerplatz anlegen' : 'Lagerplatz bearbeiten'}</h2>
            <div className="warehouse-form-grid">
              <label>Code<input name="code" required maxLength={20} defaultValue={locationEditor === 'new' ? '' : locationEditor.code} /></label>
              <label>Bezeichnung<input name="name" required defaultValue={locationEditor === 'new' ? '' : locationEditor.name} /></label>
              <label className="is-wide">Beschreibung<textarea name="description" rows={3} defaultValue={locationEditor === 'new' ? '' : locationEditor.description} /></label>
              <label className="warehouse-check"><input name="active" type="checkbox" defaultChecked={locationEditor === 'new' ? true : locationEditor.active} /> Aktiv</label>
              <label className="warehouse-check"><input name="defaultLocation" type="checkbox" defaultChecked={locationEditor === 'new' ? false : locationEditor.defaultLocation} /> Standardlagerplatz</label>
            </div>
            {locationEditor !== 'new' && locationEditor.stockedItemCount > 0 ? <p className="warehouse-hint">Dieser Lagerplatz enthält Bestand und kann deshalb nicht deaktiviert werden.</p> : null}
            {error ? <div className="warehouse-modal__error" role="alert">{error}</div> : null}
            <div className="warehouse-modal__actions"><button type="button" className="warehouse-secondary" onClick={() => setLocationEditor(null)}>Abbrechen</button><button type="submit" className="warehouse-primary" disabled={saving}>{saving ? 'Speichert …' : 'Lagerplatz speichern'}</button></div>
          </form>
        </div>
      ) : null}

      {warehouseMenu}
      {locationDeleteDialog}
    </WorkspacePage>
  );
}
